import React, { useState, useEffect, useRef } from "react"
import jMoment from "moment-jalaali"
import {
    CloseOutlined,
    CalendarOutlined,
    LeftOutlined,
    RightOutlined,
} from "@ant-design/icons"
import "./PersianDatePicker.css"

jMoment.loadPersian({ dialect: "persian-modern", usePersianDigits: false })

function PersianDatePicker({
    dateHOC,
    onChange,
    onRangeChange,
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [currentMonth, setCurrentMonth] = useState(jMoment().startOf("jMonth"))
    const [isRangePicker, setIsRangePicker] = useState(false)
    const [hoverDate, setHoverDate] = useState(null)
    const [viewMode, setViewMode] = useState("days")
    const [yearRange, setYearRange] = useState({ start: 1390, end: 1401 })
    const calendarRef = useRef(null)

    // -----------------------
    // helpers
    // -----------------------
    const convertEnglishToPersian = (str) => {
        const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"]
        return String(str).replace(
            /[0-9]/g,
            (match) => persianDigits[Number.parseInt(match)]
        )
    }

    const normalizeToJYYYYMMDD = (val) => {
        if (!val && val !== 0) return null
        if (val instanceof Date) return jMoment(val).format("jYYYY-jMM-jDD")
        if (typeof val === "number") return jMoment(val).format("jYYYY-jMM-jDD")
        if (typeof val === "string") {
            const s1 = val.replace(/\//g, "-").trim()
            let m = jMoment(s1, ["jYYYY-jMM-jDD", "jYYYY-jM-jD"], true)
            if (m.isValid()) return m.format("jYYYY-jMM-jDD")
            m = jMoment(s1)
            if (m.isValid()) return m.format("jYYYY-jMM-jDD")
            const d = new Date(val)
            if (!isNaN(d)) return jMoment(d).format("jYYYY-jMM-jDD")
            return null
        }
        return null
    }

    const formatDisplayDate = (date) => {
        if (!date) return ""
        const m = jMoment(date, "jYYYY-jMM-jDD")
        return convertEnglishToPersian(m.format("jYYYY/jMM/jDD"))
    }

    // -----------------------
    // internal states (single source of truth)
    // -----------------------
    const [selectedDate, setSelectedDate] = useState(null) // jYYYY-jMM-jDD or null
    const [range, setRange] = useState([null, null]) // [from, to]

    // -----------------------
    // useEffects grouped & conflict-free
    // 1) mount (yearRange + outside click)  <- runs once
    // 2) sync dateHOC -> state & emit callback (only when incoming prop actually changes)
    // -----------------------
    useEffect(() => {
        // mount: set yearRange and install outside click listener
        const currentYear = jMoment().jYear()
        setYearRange({ start: currentYear - 11, end: currentYear })

        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsOpen(false)
                setViewMode("days")
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // sync dateHOC -> internal state and call callbacks once (no duplicated emissions)
    useEffect(() => {
        // normalize incoming
        if (Array.isArray(dateHOC)) {
            const from = normalizeToJYYYYMMDD(dateHOC[0])
            const to = normalizeToJYYYYMMDD(dateHOC[1])

            // if already in same range and already in range mode, do nothing
            if (isRangePicker && range[0] === from && range[1] === to) {
                return
            }

            // set internal
            setIsRangePicker(true)
            setRange([from, to])
            setSelectedDate(null)
            if (from) setCurrentMonth(jMoment(from, "jYYYY-jMM-jDD").startOf("jMonth"))

            // emit once (null if both null)
            if (onRangeChange) {
                if (from || to) onRangeChange([from, to])
                else onRangeChange(null)
            }
        } else {
            const d = normalizeToJYYYYMMDD(dateHOC)

            // if already in same selectedDate and single mode, do nothing
            if (!isRangePicker && selectedDate === d) {
                return
            }

            setIsRangePicker(false)
            setSelectedDate(d)
            setRange([null, null])
            if (d) setCurrentMonth(jMoment(d, "jYYYY-jMM-jDD").startOf("jMonth"))

            // emit once (even if null)
            if (onChange) onChange(d)
        }
        // NOTE: we intentionally only depend on dateHOC here to avoid re-running on internal state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateHOC])

    // -----------------------
    // calendar generation + helpers
    // -----------------------
    const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"]
    const monthNames = [
        "فروردین",
        "اردیبهشت",
        "خرداد",
        "تیر",
        "مرداد",
        "شهریور",
        "مهر",
        "آبان",
        "آذر",
        "دی",
        "بهمن",
        "اسفند",
    ]

    const generateDays = () => {
        const firstDayOfMonth = currentMonth.clone().startOf("jMonth")
        const jYear = currentMonth.jYear()
        const jMonth = currentMonth.jMonth()
        const daysInMonth = jMoment.jDaysInMonth(jYear, jMonth)
        const firstDayOfWeek = firstDayOfMonth.day()
        const startingDay = firstDayOfWeek === 6 ? 0 : firstDayOfWeek + 1

        const days = []
        const prevMonth = currentMonth.clone().subtract(1, "jMonth")
        const prevDays = jMoment.jDaysInMonth(prevMonth.jYear(), prevMonth.jMonth())
        for (let i = 0; i < startingDay; i++) {
            const d = prevDays - startingDay + i + 1
            const date = prevMonth.clone().jDate(d).format("jYYYY-jMM-jDD")
            days.push({ day: d, month: "prev", date })
        }

        const today = jMoment().format("jYYYY-jMM-jDD")
        for (let i = 1; i <= daysInMonth; i++) {
            const date = currentMonth.clone().jDate(i).format("jYYYY-jMM-jDD")
            days.push({ day: i, month: "current", date, isToday: date === today })
        }

        const totalDaysAdded = startingDay + daysInMonth
        const nextDays = 42 - totalDaysAdded
        const nextMonth = currentMonth.clone().add(1, "jMonth")
        for (let i = 1; i <= nextDays; i++) {
            const date = nextMonth.clone().jDate(i).format("jYYYY-jMM-jDD")
            days.push({ day: i, month: "next", date })
        }

        return days
    }

    // -----------------------
    // events (handlers call callbacks immediately on user actions)
    // -----------------------
    const handleDateClick = (date) => {
        if (isRangePicker) {
            const [from, to] = range
            if (!from || (from && to)) {
                // start a new range
                setRange([date, null])
            } else {
                const f = jMoment(from, "jYYYY-jMM-jDD")
                const d = jMoment(date, "jYYYY-jMM-jDD")
                if (d.isBefore(f)) {
                    console.log("first")
                    setRange([date, from])
                    onRangeChange && onRangeChange([date, from])
                } else {
                    // console.log("second")
                    setRange([from, date])
                    onRangeChange && onRangeChange([from, date])
                }
                setIsOpen(false)
            }
        } else {
            setSelectedDate(date)
            onChange && onChange(date)
            setIsOpen(false)
        }
    }

    const clearDate = () => {
        if (isRangePicker) {
            setRange([null, null])
            onRangeChange && onRangeChange(null)
        } else {
            setSelectedDate(null)
            onChange && onChange(null)
        }
    }

    const setToday = () => {
        const today = jMoment().format("jYYYY-jMM-jDD")
        if (isRangePicker) {
            setRange([today, today])
            onRangeChange && onRangeChange([today, today])
        } else {
            setSelectedDate(today)
            onChange && onChange(today)
        }
        setIsOpen(false)
    }

    const isInRange = (date) => {
        const [from, to] = range
        if (!from) return false
        if (to) return date >= from && date <= to
        if (hoverDate) {
            const start = from < hoverDate ? from : hoverDate
            const end = from < hoverDate ? hoverDate : from
            return date >= start && date <= end
        }
        return date === from
    }

    // -----------------------
    // rendering
    // -----------------------
    const days = generateDays()
    const currentYear = jMoment().jYear()

    return (
        <div className="datepicker-container" dir="rtl" ref={calendarRef}>
            <div className="datepicker-input-wrapper">
                {isRangePicker ? (
                    <div className="range-wrapper">
                        <div className="date-input" onClick={() => setIsOpen(!isOpen)}>
                            <span>{range[0] ? formatDisplayDate(range[0]) : "از تاریخ"}</span>
                            {range[0] && (
                                <button
                                    className="clear-btn"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        clearDate()
                                    }}
                                >
                                    <CloseOutlined />
                                </button>
                            )}
                            <CalendarOutlined className="calendar-icon" />
                        </div>
                        <div className="to-label">تا</div>
                        <div className="date-input" onClick={() => setIsOpen(!isOpen)}>
                            <span>{range[1] ? formatDisplayDate(range[1]) : "تا تاریخ"}</span>
                            {range[1] && (
                                <button
                                    className="clear-btn"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setRange([range[0], null])
                                    }}
                                >
                                    <CloseOutlined />
                                </button>
                            )}
                            <CalendarOutlined className="calendar-icon" />
                        </div>
                    </div>
                ) : (
                    <div className="date-input" onClick={() => setIsOpen(!isOpen)}>
                        <span>
                            {selectedDate ? formatDisplayDate(selectedDate) : "انتخاب تاریخ"}
                        </span>
                        {selectedDate && (
                            <button
                                className="clear-btn"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    clearDate()
                                }}
                            >
                                <CloseOutlined />
                            </button>
                        )}
                        <CalendarOutlined className="calendar-icon" />
                    </div>
                )}

                {isOpen && (
                    <div className="calendar-popup">
                        <div className="calendar-inner">
                            <div className="calendar-header">
                                <button
                                    className="nav-btn"
                                    onClick={() =>
                                        setCurrentMonth(currentMonth.clone().subtract(1, "jMonth"))
                                    }
                                >
                                    <RightOutlined />
                                </button>
                                <div className="calendar-title">
                                    {monthNames[currentMonth.jMonth()]}{" "}
                                    {convertEnglishToPersian(currentMonth.jYear().toString())}
                                </div>
                                <button
                                    className="nav-btn"
                                    onClick={() =>
                                        setCurrentMonth(currentMonth.clone().add(1, "jMonth"))
                                    }
                                >
                                    <LeftOutlined />
                                </button>
                            </div>

                            <div className="calendar-grid">
                                {weekDays.map((d, i) => (
                                    <div key={i} className="weekday">
                                        {d}
                                    </div>
                                ))}

                                {days.map((day, idx) => (
                                    <div
                                        key={idx}
                                        className={`day-cell 
                      ${day.month !== "current" ? "other-month" : ""}
                      ${day.isToday ? "today" : ""}
                      ${!isRangePicker && day.date === selectedDate
                                                ? "selected"
                                                : ""
                                            }
                      ${isRangePicker &&
                                                (day.date === range[0] || day.date === range[1])
                                                ? "selected"
                                                : ""
                                            }
                      ${isInRange(day.date) &&
                                                day.date !== range[0] &&
                                                day.date !== range[1]
                                                ? "in-range"
                                                : ""
                                            }`}
                                        onClick={() =>
                                            day.month === "current" && handleDateClick(day.date)
                                        }
                                        onMouseEnter={() => setHoverDate(day.date)}
                                        onMouseLeave={() => setHoverDate(null)}
                                    >
                                        {convertEnglishToPersian(day.day.toString())}
                                    </div>
                                ))}
                            </div>

                            <div className="quick-actions">
                                <button className="quick-btn blue" onClick={setToday}>
                                    امروز
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default React.memo(PersianDatePicker)